/**
 * AssetIconStep Component
 *
 * Step for selecting an icon override for asset nodes.
 * Used as step 2 in the Asset wizard.
 *
 * Features:
 * - Comprehensive IconPicker with search, categories, and virtualization
 * - Full keyboard navigation support
 * - Screen reader accessibility (ARIA radiogroup pattern)
 * - Support for 131+ icons from AWS, Azure, GCP, AD, and Lucide registries
 */

import { useCallback } from 'react';

import type { AssetWizardFormData } from '@/sections/attacks/attackPaths/components/wizards/types';

import { IconPicker, type IconSelection } from './IconPicker';

/**
 * Props for AssetIconStep component
 */
interface AssetIconStepProps {
  /** Current wizard form data */
  formData: AssetWizardFormData;
  /** Form data setter */
  setFormData: (
    updater:
      | AssetWizardFormData
      | ((prev: AssetWizardFormData) => AssetWizardFormData)
  ) => void;
}

/**
 * Icon selection step for Asset wizard.
 *
 * Provides a comprehensive icon picker with search, category filtering,
 * and virtualized grid for 131+ icons.
 *
 * @example
 * ```tsx
 * <AssetIconStep
 *   formData={formData}
 *   setFormData={setFormData}
 * />
 * ```
 */
export function AssetIconStep({ formData, setFormData }: AssetIconStepProps) {
  /**
   * Handle icon selection from IconPicker
   */
  const handleIconChange = useCallback(
    (selection: IconSelection) => {
      setFormData(prev => ({
        ...prev,
        iconOverride: selection.id || undefined,
      }));
    },
    [setFormData]
  );

  return (
    <div className="flex flex-col gap-6" data-testid="asset-icon-step">
      {/* Header */}
      <div>
        <h3
          id="icon-selection-label"
          className="text-chariot-text-primary mb-2 text-lg font-semibold"
        >
          Choose an Icon
        </h3>
        <p
          id="icon-selection-desc"
          className="text-chariot-text-secondary text-sm"
        >
          Select an icon to represent this asset in the attack path diagram.
        </p>
      </div>

      {/* Icon Picker with search, categories, and virtualized grid */}
      <IconPicker value={formData.iconOverride} onChange={handleIconChange} />
    </div>
  );
}
